import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, Int, ID } from 'type-graphql'

import { Domain, ScalarObject } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { Kpi, KpiPeriodType } from '../kpi/kpi'
import { KpiOrgScope } from '../kpi-org-scope/kpi-org-scope'

@Entity()
@Index('ix_kpi_statistic_target', (kpiStatistic: KpiStatistic) => [kpiStatistic.domain, kpiStatistic.kpi])
@Index('ix_kpi_statistic_period', (kpiStatistic: KpiStatistic) => [
  kpiStatistic.domain,
  kpiStatistic.valueDate,
  kpiStatistic.periodType
])
@Index('ix_kpi_statistic_scope', (kpiStatistic: KpiStatistic) => [
  kpiStatistic.domain,
  kpiStatistic.kpi,
  kpiStatistic.kpiOrgScope,
  kpiStatistic.valueDate
])
@Index('ix_kpi_statistic_legacy_scope', (kpiStatistic: KpiStatistic) => [
  kpiStatistic.domain,
  kpiStatistic.kpi,
  kpiStatistic.scope,
  kpiStatistic.valueDate
])
@ObjectType({
  description:
    'KPI Statistics Entity - Stores comprehensive statistical information for KPIs and Categories including central tendency measures (mean, median), dispersion metrics (standard deviation, variance), range indicators (min, max), and percentile distributions (25th, 75th percentiles, IQR). Supports both KPI and Category targets with flexible period-based aggregation (daily, weekly, monthly, yearly). Includes extensible JSON fields for additional metrics and metadata for calculation tracking.'
})
export class KpiStatistic {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID)
  readonly id: string

  // === 대상 정보 ===

  @ManyToOne(() => Kpi)
  @Field(type => Kpi, {
    nullable: true,
    description: 'Reference to the KPI definition for which this value is calculated.'
  })
  kpi: Kpi

  @RelationId((kpiStatistic: KpiStatistic) => kpiStatistic.kpi)
  kpiId: string

  // === 통계 기간 ===
  @Column()
  @Field({
    description:
      'Date or period for which this statistic is calculated (e.g., day: YYYY-MM-DD, month: YYYY-MM, quarter: YYYY-Qn, range: YYYY-MM-DD~YYYY-MM-DD).'
  })
  valueDate: string

  @Column({ default: 'DAY' })
  @Field(type => KpiPeriodType, { description: 'Aggregation period type for this statistic.' })
  periodType: KpiPeriodType

  // === 스코프 정보 ===
  
  @ManyToOne(() => KpiOrgScope, { nullable: true })
  @Field(type => KpiOrgScope, {
    nullable: true,
    description: 'Organization scope for scoped statistics. Null for overall statistics.'
  })
  kpiOrgScope?: KpiOrgScope

  @RelationId((kpiStatistic: KpiStatistic) => kpiStatistic.kpiOrgScope)
  kpiOrgScopeId?: string

  @Column({ nullable: true })
  @Field({ 
    nullable: true, 
    description: 'Legacy scope field - use kpiOrgScope instead. Statistical scope - null for overall statistics, category value for scoped statistics (e.g., "서울", "부장", "대규모")'
  })
  scope?: string

  // === 핵심 통계 필드 (14개) ===

  // 1. 기본 정보 (3개)
  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Number of data points used in calculation' })
  count?: number // 데이터 개수

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Sum of all values in the dataset' })
  sum?: number // 합계

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Range of values (maximum - minimum)' })
  range?: number // 범위

  // 2. 중심 경향 (2개)
  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Arithmetic mean of all values' })
  mean?: number // 평균

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Middle value when data is sorted (50th percentile)' })
  median?: number // 중앙값

  // 3. 범위 (2개)
  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Minimum value in the dataset' })
  minimum?: number // 최소값

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Maximum value in the dataset' })
  maximum?: number // 최대값

  // 4. 분산 (2개)
  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Standard deviation - measure of data dispersion' })
  standardDeviation?: number // 표준편차

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Variance - square of standard deviation' })
  variance?: number // 분산

  // 5. 분위수 (5개)
  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: '25th percentile - 25% of data is below this value' })
  percentile25?: number // 25분위수

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: '75th percentile - 75% of data is below this value' })
  percentile75?: number // 75분위수

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Interquartile range (75th percentile - 25th percentile)' })
  iqr?: number // 사분위수 범위

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Lower fence for outlier detection (Q1 - 1.5 * IQR)' })
  lowerFence?: number // 하위 울타리

  @Column({ type: 'float', nullable: true })
  @Field({ nullable: true, description: 'Upper fence for outlier detection (Q3 + 1.5 * IQR)' })
  upperFence?: number // 상위 울타리

  // === 확장 가능한 필드 (JSON) ===
  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Additional statistical metrics stored as key-value pairs'
  })
  additionalStatistics: {
    [metricName: string]: number
  }

  // === 메타데이터 (JSON) ===
  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description: 'Calculation metadata including method, timestamp, and data quality information'
  })
  metadata: { [key: string]: any }

  // === 표준 필드들 ===
  @ManyToOne(type => Domain)
  @Field({ nullable: true, description: 'Domain this statistic belongs to' })
  domain?: Domain

  @RelationId((kpiStatistic: KpiStatistic) => kpiStatistic.domain)
  domainId?: string

  @CreateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this statistic was created' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this statistic was last updated' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who created this statistic' })
  creator?: User

  @RelationId((kpiStatistic: KpiStatistic) => kpiStatistic.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who last updated this statistic' })
  updater?: User

  @RelationId((kpiStatistic: KpiStatistic) => kpiStatistic.updater)
  updaterId?: string
}
